/**
 * CriacaoPeticaoInicial - Wizard para criar petição inicial (reclamante)
 *
 * Implementa fluxo de 6 etapas conforme TAREFA-007:
 * 1. Contexto do caso
 * 2. Anexar documentos
 * 3. Processamento inicial (loading)
 * 4. Informações adicionais
 * 5. Processamento final (loading)
 * 6. Resultados
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { FileText, Upload, Loader2, ClipboardList, BarChart3, CheckCircle2 } from 'lucide-react';
import type {
  EtapaWizardCriacao,
  ContextoInicialPeticao,
  DocumentoAnexado,
  InformacoesAdicionaisSolicitadas,
  ResultadoCriacaoPeticao,
  AnalisePreditiva,
  ProximosPassosEstrategicos,
  PeticaoInicialGerada,
  StatusProcessamentoCriacao,
} from '../tipos/tiposCriacaoPeticao';
import { ComponenteContextoInicial } from '../componentes/criacao-peticao/ComponenteContextoInicial';
import { ComponenteUploadDocumentos } from '../componentes/criacao-peticao/ComponenteUploadDocumentos';
import ComponenteLoadingProcessamento from '../componentes/criacao-peticao/ComponenteLoadingProcessamento';
import { ComponenteInformacoesAdicionais } from '../componentes/criacao-peticao/ComponenteInformacoesAdicionais';
import { ComponenteResultadosAnalise } from '../componentes/criacao-peticao/ComponenteResultadosAnalise';
import { ErrorBoundary } from '../../../core/componentes/comuns/ErrorBoundary';
import { criarProcesso, obterProcesso, atualizarProcesso } from '../servicos/servicoApiProcessos';
import { clienteApi } from '../../../core/servicos/clienteApi';
import { obterWebSocketService, type AtualizacaoStatusAnalise } from '../../../core/servicos/websocketService';
import { useAutenticacao } from '../../../core/contextos/ContextoAutenticacao';
import { TipoVerticalProcesso } from '../tipos/tiposVerticais';

type StatusEtapa = 'pending' | 'in-progress' | 'completed' | 'error';

interface MetadataEtapa {
  numero: EtapaWizardCriacao;
  titulo: string;
  descricao: string;
  icone: React.ComponentType<{ className?: string }>; // lucide icon component
  status: StatusEtapa;
}

interface WizardPersistedState {
  etapa?: EtapaWizardCriacao;
  processoId?: string;
  contextoInicial?: ContextoInicialPeticao;
  documentosAnexados?: DocumentoAnexado[];
  timestamp?: number;
}

const WIZARD_STORAGE_PREFIX = 'wizard_criacao_peticao_state_v2:';
const LEGACY_WIZARD_STORAGE_KEY = 'wizard_criacao_peticao_state';

const arrayOrEmpty = <T,>(valor: T[] | undefined | null): T[] => (Array.isArray(valor) ? valor : []);

const textoOu = (valor: unknown, fallback = ''): string => (typeof valor === 'string' ? valor : fallback);
const textoOuOpcional = (valor: unknown): string | undefined => (typeof valor === 'string' ? valor : undefined);

const numeroOu = (valor: unknown, fallback = 0): number => {
  if (typeof valor === 'number' && Number.isFinite(valor)) return valor;
  if (typeof valor === 'string') {
    const convertido = Number(valor);
    if (Number.isFinite(convertido)) return convertido;
  }
  return fallback;
};

const numeroOpcional = (valor: unknown): number | undefined => {
  if (typeof valor === 'number' && Number.isFinite(valor)) return valor;
  if (typeof valor === 'string') {
    const convertido = Number(valor);
    if (Number.isFinite(convertido)) return convertido;
  }
  return undefined;
};

const obterValor = (fonte: Record<string, unknown> | null | undefined, ...chaves: string[]): unknown => {
  if (!fonte) return undefined;
  for (const chave of chaves) {
    if (Object.prototype.hasOwnProperty.call(fonte, chave)) {
      const valor = fonte[chave];
      if (valor !== undefined && valor !== null) return valor;
    }
  }
  return undefined;
};

const normalizarStatusProcessamento = (status: string | undefined): StatusProcessamentoCriacao => {
  const chave = (status ?? '').toLowerCase();
  const mapa: Record<string, StatusProcessamentoCriacao> = {
    aguardando_contexto: 'aguardandoContexto',
    aguardandocontexto: 'aguardandoContexto',
    aguardando_documentos: 'aguardandoDocumentos',
    aguardandodocumentos: 'aguardandoDocumentos',
    processando_inicial: 'processandoInicial',
    processandoinicial: 'processandoInicial',
    aguardando_informacoes_adicionais: 'aguardandoInformacoesAdicionais',
    aguardandoinformacoesadicionais: 'aguardandoInformacoesAdicionais',
    processando_final: 'processandoFinal',
    processandofinal: 'processandoFinal',
    gerando_peticoes: 'gerandoPeticoes',
    gerandopeticoes: 'gerandoPeticoes',
    concluido: 'concluido',
    concluida: 'concluido',
    erro: 'erro',
  };
  return mapa[chave] ?? 'concluido';
};

const normalizarContextoDoBackend = (ctx: Record<string, unknown> | null | undefined): ContextoInicialPeticao => ({
  descricaoCaso: textoOu(obterValor(ctx, 'descricaoCaso', 'descricao_caso'), ''),
  tipoAcao: textoOuOpcional(obterValor(ctx, 'tipoAcao', 'tipo_acao')),
  parteAutora: textoOu(obterValor(ctx, 'parteAutora', 'parte_autora'), ''),
  parteRe: textoOuOpcional(obterValor(ctx, 'parteRe', 'parte_re')),
  pedidosPretendidos: textoOuOpcional(obterValor(ctx, 'pedidosPretendidos', 'pedidos_pretendidos')),
  valorCausaEstimado: numeroOpcional(obterValor(ctx, 'valorCausaEstimado', 'valor_causa_estimado')),
  observacoes: textoOuOpcional(obterValor(ctx, 'observacoes')),
});

const normalizarDocumentosAnexados = (lista: unknown): DocumentoAnexado[] => arrayOrEmpty(lista as Record<string, unknown>[]).map((doc, indice) => {
  const idRaw = obterValor(doc, 'id', 'documento_id', 'uuid', '_id') ?? `doc-${indice}-${Date.now()}`;
  const categoria = textoOuOpcional(obterValor(doc, 'categoria')) as DocumentoAnexado['categoria'] | undefined;
  return {
    id: String(idRaw),
    nome: textoOu(obterValor(doc, 'nome', 'nome_arquivo') ?? 'Documento'),
    tipo: textoOu(obterValor(doc, 'tipo', 'mime_type', 'content_type') ?? 'application/octet-stream'),
    tamanho: numeroOu(obterValor(doc, 'tamanho', 'tamanho_bytes') ?? 0, 0),
    dataUpload: textoOu(obterValor(doc, 'dataUpload', 'data_upload') ?? new Date().toISOString(), new Date().toISOString()),
    categoria,
    descricao: textoOuOpcional(obterValor(doc, 'descricao', 'descricao_curta')),
  } satisfies DocumentoAnexado;
});

const normalizarAnalisePreditiva = (entrada: Record<string, unknown> | null | undefined): AnalisePreditiva => ({
  probabilidadeSucesso: numeroOu(obterValor(entrada, 'probabilidadeSucesso', 'probabilidade_sucesso') ?? 0, 0),
  probabilidadeDerrota: numeroOu(obterValor(entrada, 'probabilidadeDerrota', 'probabilidade_derrota') ?? 0, 0),
  probabilidadeAcordo: numeroOu(obterValor(entrada, 'probabilidadeAcordo', 'probabilidade_acordo') ?? 0, 0),
  probabilidadeDerrotaParcial: numeroOu(obterValor(entrada, 'probabilidadeDerrotaParcial', 'probabilidade_derrota_parcial') ?? 0, 0),
  fundamentos: arrayOrEmpty((obterValor(entrada, 'fundamentos') as string[]) ?? []),
  riscosIdentificados: arrayOrEmpty((obterValor(entrada, 'riscosIdentificados', 'riscos_identificados') as string[]) ?? []),
  pontosFortes: arrayOrEmpty((obterValor(entrada, 'pontosFortes', 'pontos_fortes') as string[]) ?? []),
  pontosFracos: arrayOrEmpty((obterValor(entrada, 'pontosFracos', 'pontos_fracos') as string[]) ?? []),
  jurisprudenciaFavoravel: numeroOpcional(obterValor(entrada, 'jurisprudenciaFavoravel', 'jurisprudencia_favoravel')),
  valorEstimadoMin: numeroOpcional(obterValor(entrada, 'valorEstimadoMin', 'valor_estimado_min')),
  valorEstimadoMax: numeroOpcional(obterValor(entrada, 'valorEstimadoMax', 'valor_estimado_max')),
});

const normalizarProximosPassos = (entrada: Record<string, unknown> | null | undefined): ProximosPassosEstrategicos => ({
  passos: arrayOrEmpty((obterValor(entrada, 'passos') as Record<string, unknown>[]) ?? []).map((passo, idx) => {
    const importancia = textoOuOpcional(obterValor(passo, 'importancia', 'nivel_importancia')) as ProximosPassosEstrategicos['passos'][number]['importancia'] | undefined;
    return {
      ordem: numeroOu(obterValor(passo, 'ordem', 'ordem_sugerida', 'indice') ?? idx + 1, idx + 1),
      titulo: textoOu(obterValor(passo, 'titulo') ?? `Passo ${idx + 1}`),
      descricao: textoOu(obterValor(passo, 'descricao') ?? ''),
      prazoSugerido: textoOuOpcional(obterValor(passo, 'prazoSugerido', 'prazo_sugerido')),
      importancia,
    };
  }),
  recomendacoesGerais: arrayOrEmpty((obterValor(entrada, 'recomendacoesGerais', 'recomendacoes_gerais') as string[]) ?? []),
  documentosAdicionaisRecomendados: arrayOrEmpty((obterValor(entrada, 'documentosAdicionaisRecomendados', 'documentos_adicionais_recomendados') as string[]) ?? []),
});

const normalizarPeticoesGeradas = (entrada: unknown): PeticaoInicialGerada[] => arrayOrEmpty((entrada as Record<string, unknown>[]) ?? []).map((peticao, idx) => {
  const idRaw = obterValor(peticao, 'id', 'peticao_id') ?? `pet-${idx}-${Date.now()}`;
  return {
    id: String(idRaw),
    estrategia: (textoOu(obterValor(peticao, 'estrategia') ?? 'conservadora') as PeticaoInicialGerada['estrategia']) || 'conservadora',
    titulo: textoOu(obterValor(peticao, 'titulo') ?? 'Petição Gerada'),
    conteudoCompleto: textoOu(obterValor(peticao, 'conteudoCompleto', 'conteudo_completo') ?? ''),
    probabilidadeSucesso: numeroOu(obterValor(peticao, 'probabilidadeSucesso', 'probabilidade_sucesso') ?? 0, 0),
    fundamentacaoEstrategia: textoOu(obterValor(peticao, 'fundamentacaoEstrategia', 'fundamentacao_estrategia') ?? ''),
    pontosChave: arrayOrEmpty((obterValor(peticao, 'pontosChave', 'pontos_chave') as string[]) ?? []),
    documentosCitados: arrayOrEmpty((obterValor(peticao, 'documentosCitados', 'documentos_citados') as string[]) ?? []),
    tempoGeracao: numeroOpcional(obterValor(peticao, 'tempoGeracao', 'tempo_geracao')),
  } satisfies PeticaoInicialGerada;
});

const normalizarResultadoCriacao = (resultadoBackend: Record<string, unknown>): ResultadoCriacaoPeticao => {
  const contextoRaw = (resultadoBackend.contextoInicial ?? resultadoBackend.contexto_inicial) as Record<string, unknown> | undefined;
  const analiseRaw = (resultadoBackend.analisePreditiva ?? resultadoBackend.analise_preditiva) as Record<string, unknown> | undefined;
  const proximosRaw = (resultadoBackend.proximosPassos ?? resultadoBackend.proximos_passos) as Record<string, unknown> | undefined;
  return {
    peticaoId: textoOu(resultadoBackend.peticaoId ?? resultadoBackend.peticao_id ?? ''),
    contextoInicial: normalizarContextoDoBackend(contextoRaw),
    documentosAnexados: normalizarDocumentosAnexados(resultadoBackend.documentosAnexados ?? resultadoBackend.documentos_anexados),
    informacoesAdicionaisFornecidas: (resultadoBackend.informacoesAdicionaisFornecidas ?? resultadoBackend.informacoes_adicionais_fornecidas) as Record<string, unknown> | undefined,
    analisePreditiva: normalizarAnalisePreditiva(analiseRaw),
    proximosPassos: normalizarProximosPassos(proximosRaw),
    peticoesGeradas: normalizarPeticoesGeradas(resultadoBackend.peticoesGeradas ?? resultadoBackend.peticoes_geradas),
    dataCriacao: textoOu(resultadoBackend.dataCriacao ?? resultadoBackend.data_criacao ?? new Date().toISOString(), new Date().toISOString()),
    status: normalizarStatusProcessamento(resultadoBackend.status as string | undefined),
    mensagemErro: textoOuOpcional(resultadoBackend.mensagemErro ?? resultadoBackend.mensagem_erro ?? undefined),
  } satisfies ResultadoCriacaoPeticao;
};

export function CriacaoPeticaoInicial(): JSX.Element {
  const { token } = useAutenticacao();
  const tokenRef = useRef(token);
  useEffect(() => { tokenRef.current = token; }, [token]);

  const location = useLocation();
  const navigate = useNavigate();

  // ===== STATES =====
  const [etapaAtual, setEtapaAtual] = useState<EtapaWizardCriacao>(1);
  const [processoId, setProcessoId] = useState<string>('');
  const [contextoInicial, setContextoInicial] = useState<ContextoInicialPeticao | null>(null);
  const [documentosAnexados, setDocumentosAnexados] = useState<DocumentoAnexado[]>([]);
  const [informacoesAdicionaisSolicitadas, setInfoAdicionais] = useState<InformacoesAdicionaisSolicitadas | null>(null);
  const [informacoesAdicionaisFornecidas, setInfoFornecidas] = useState<Record<string, unknown>>({});
  const [resultado, setResultado] = useState<ResultadoCriacaoPeticao | null>(null);

  const [progressoProcessamento, setProgressoProcessamento] = useState<number>(0);
  const [etapaProcessamento, setEtapaProcessamento] = useState<string>('');
  const [erro, setErro] = useState<string>('');

  type WsRefType = { servico: ReturnType<typeof obterWebSocketService>; unsubscribe: () => void } | null;
  const wsRef = useRef<WsRefType>(null);
  const processoSincronizadoRef = useRef<string | null>(null);
  const processoIdRef = useRef(processoId);
  useEffect(() => { processoIdRef.current = processoId; }, [processoId]);
  const processosHidratadosRef = useRef<Set<string>>(new Set());

  const obterStorageKey = useCallback((pid?: string) => {
    const alvo = pid ?? processoIdRef.current;
    return alvo ? `${WIZARD_STORAGE_PREFIX}${alvo}` : null;
  }, []);

  const sincronizarProcessoNaUrl = useCallback((pid?: string, opcoes?: { replace?: boolean }) => {
    const params = new URLSearchParams(location.search);
    if (pid) {
      if (params.get('processo_id') === pid) return;
      params.set('processo_id', pid);
    } else if (!params.has('processo_id')) {
      return;
    } else {
      params.delete('processo_id');
    }
    const novaBusca = params.toString();
    const destino = `${location.pathname}${novaBusca ? `?${novaBusca}` : ''}`;
    navigate(destino, { replace: opcoes?.replace ?? true });
  }, [location.pathname, location.search, navigate]);

  const serializarContextoParaBackend = useCallback((ctx: ContextoInicialPeticao | null) => {
    if (!ctx) return null;
    const payload: Record<string, unknown> = {
      descricao_caso: ctx.descricaoCaso,
      parte_autora: ctx.parteAutora,
    };
    if (ctx.tipoAcao) payload.tipo_acao = ctx.tipoAcao;
    if (ctx.parteRe) payload.parte_re = ctx.parteRe;
    if (ctx.pedidosPretendidos) payload.pedidos_pretendidos = ctx.pedidosPretendidos;
    if (typeof ctx.valorCausaEstimado === 'number') payload.valor_causa_estimado = ctx.valorCausaEstimado;
    if (ctx.observacoes) payload.observacoes = ctx.observacoes;
    return payload;
  }, []);

  const extrairIdsDocumentos = useCallback((docs: DocumentoAnexado[]) => docs
    .map((doc) => doc.id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0), []);

  // ===== STEP METADATA =====
  const etapas: MetadataEtapa[] = [
    { numero: 1, titulo: 'Contexto do Caso', descricao: 'Descreva sua situação', icone: FileText, status: etapaAtual === 1 ? 'in-progress' : etapaAtual > 1 ? 'completed' : 'pending' },
    { numero: 2, titulo: 'Anexar Documentos', descricao: 'Envie documentos relevantes', icone: Upload, status: etapaAtual === 2 ? 'in-progress' : etapaAtual > 2 ? 'completed' : 'pending' },
    { numero: 3, titulo: 'Processamento Inicial', descricao: 'IA analisando informações', icone: Loader2, status: etapaAtual === 3 ? 'in-progress' : etapaAtual > 3 ? 'completed' : 'pending' },
    { numero: 4, titulo: 'Informações Adicionais', descricao: 'Complete dados solicitados', icone: ClipboardList, status: etapaAtual === 4 ? 'in-progress' : etapaAtual > 4 ? 'completed' : 'pending' },
    { numero: 5, titulo: 'Análise Final', descricao: 'Gerando análises e petições', icone: BarChart3, status: etapaAtual === 5 ? 'in-progress' : etapaAtual > 5 ? 'completed' : 'pending' },
    { numero: 6, titulo: 'Resultados', descricao: 'Análise preditiva e petições', icone: CheckCircle2, status: etapaAtual === 6 ? 'in-progress' : 'pending' },
  ];

  // ===== Navigation helpers =====
  const persistirEstadoWizard = useCallback((etapa: number) => {
    const storageKey = obterStorageKey();
    if (!storageKey) return;
    try {
      const estado = {
        etapa,
        processoId: processoIdRef.current,
        contextoInicial,
        documentosAnexados,
        timestamp: Date.now(),
      } as WizardPersistedState;
      localStorage.setItem(storageKey, JSON.stringify(estado));
    } catch (err) {
      console.warn('[CriacaoPeticaoInicial] Falha ao persistir estado no localStorage', err);
    }
  }, [contextoInicial, documentosAnexados, obterStorageKey]);

  const avancarEtapa = useCallback(() => {
    if (etapaAtual < 6) {
      setEtapaAtual((prev) => (prev + 1) as EtapaWizardCriacao);
      persistirEstadoWizard(etapaAtual + 1);
    }
  }, [etapaAtual, persistirEstadoWizard]);

  const voltarEtapa = useCallback(() => {
    if (etapaAtual > 1) {
      setEtapaAtual((prev) => (prev - 1) as EtapaWizardCriacao);
      persistirEstadoWizard(etapaAtual - 1);
    }
  }, [etapaAtual, persistirEstadoWizard]);

  const limparEstadoPersistido = useCallback((pidAlvo?: string) => {
    const storageKey = obterStorageKey(pidAlvo);
    if (storageKey) {
      localStorage.removeItem(storageKey);
    }
    // Migração: remover legacy keys
    localStorage.removeItem(LEGACY_WIZARD_STORAGE_KEY);
  }, [obterStorageKey]);

  const resetarFluxo = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.unsubscribe();
      wsRef.current.servico.release();
      wsRef.current = null;
    }
    const pidAnterior = processoIdRef.current;
    processoSincronizadoRef.current = null;
    setEtapaAtual(1);
    setProcessoId('');
    setContextoInicial(null);
    setDocumentosAnexados([]);
    setInfoAdicionais(null);
    setInfoFornecidas({});
    setResultado(null);
    setProgressoProcessamento(0);
    setEtapaProcessamento('');
    setErro('');
    limparEstadoPersistido(pidAnterior);
    sincronizarProcessoNaUrl(undefined);
  }, [limparEstadoPersistido, sincronizarProcessoNaUrl]);

  const cancelarProcessamentoAtual = useCallback(async () => {
    if (!processoId) return;
    try {
      await clienteApi.delete(`/api/juridico/criacao-peticao/${encodeURIComponent(processoId)}/cancelar`);
    } catch (err) {
      console.warn('[CriacaoPeticaoInicial] Falha ao cancelar processo atual', err);
    }
  }, [processoId]);

  const reiniciarFluxo = useCallback(async () => {
    await cancelarProcessamentoAtual();
    resetarFluxo();
  }, [cancelarProcessamentoAtual, resetarFluxo]);

  const tratarFalhaProcessamento = useCallback(async (
    mensagem?: string,
    voltarParaEtapa: EtapaWizardCriacao = 2,
    opcoes?: { cancelarProcesso?: boolean }
  ) => {
    if (opcoes?.cancelarProcesso !== false) {
      await cancelarProcessamentoAtual();
    }
    setEtapaAtual(voltarParaEtapa);
    setProgressoProcessamento(0);
    setEtapaProcessamento('');
    setErro(mensagem ?? 'Ocorreu um erro durante o processamento. Revise os dados e tente novamente.');
    if (voltarParaEtapa <= 2) {
      setInfoAdicionais(null);
      setInfoFornecidas({});
    }
    persistirEstadoWizard(voltarParaEtapa);
  }, [cancelarProcessamentoAtual, persistirEstadoWizard]);

  const voltarParaUpload = useCallback(() => {
    setEtapaAtual(2);
    setErro('');
    persistirEstadoWizard(2);
  }, [persistirEstadoWizard]);

  const handleProcessamentoErro = useCallback((motivo?: string) => {
    const fallback: EtapaWizardCriacao = etapaAtual >= 5 ? 4 : 2;
    let mensagem: string | undefined;
    if (motivo === 'cancelado_pelo_usuario') {
      mensagem = 'Processamento cancelado pelo usuário.';
    } else if (motivo === 'timeout') {
      mensagem = 'Processamento demorou mais que o esperado. Tente novamente.';
    }
    void tratarFalhaProcessamento(mensagem, fallback);
  }, [etapaAtual, tratarFalhaProcessamento]);



  // ===== Backend Integration =====
  const criarProcessoBackend = useCallback(async (contexto: ContextoInicialPeticao): Promise<string | null> => {
    try {
      const resp = await criarProcesso({
        nome: 'Criacao Peticao Reclamante',
        descricao: 'Criacao via wizard front',
        tipo_vertical: TipoVerticalProcesso.CRIACAO_PETICAO // TAREFA-224: Identificar corretamente o tipo
      } as unknown as Record<string, unknown>);
      const dataObj = resp.data as unknown as { processo_id?: string; id?: string };
      const pid = dataObj?.processo_id || dataObj?.id || '';
      setProcessoId(pid);
      sincronizarProcessoNaUrl(pid);

      // Persist metadados: contexto inicial
      try {
        await atualizarProcesso(pid, { metadados: { contexto_inicial: contexto } });
      } catch (err) {
        // Non-blocking
        console.warn('[CriacaoPeticaoInicial] Falha ao persistir metadados do contexto', err);
      }
      return pid;
    } catch (err) {
      console.error('Erro ao criar processo', err);
      setErro('Erro ao criar processo');
      return null;
    }
  }, [sincronizarProcessoNaUrl]);

  const iniciarProcessamentoInicial = async (docs?: DocumentoAnexado[]): Promise<boolean> => {
    if (!processoId) {
      setErro('ID do processo não encontrado');
      return false;
    }

    const documentosIds = extrairIdsDocumentos(docs ?? documentosAnexados);

    try {
      const contextoPayload = serializarContextoParaBackend(contextoInicial);
      if (!contextoPayload) {
        await tratarFalhaProcessamento('Contexto inicial não encontrado. Recomece o fluxo.', 1);
        return false;
      }
      await clienteApi.post(`/api/juridico/criacao-peticao/${encodeURIComponent(processoId)}/processar-inicial`, {
        contexto: contextoPayload,
        documentos: documentosIds,
      });
      setErro('');
      conectarWebSocket(processoId);
      return true;
    } catch (err) {
      console.error('Erro ao iniciar processamento inicial', err);
      const mensagem = axios.isAxiosError(err)
        ? (err.response?.data?.detail as string) || 'Erro ao iniciar processamento'
        : 'Erro ao iniciar processamento';
      await tratarFalhaProcessamento(mensagem, 2);
      return false;
    }
  };

  const buscarInformacoesAdicionais = useCallback(async (opcoes?: { fallbackEtapa?: EtapaWizardCriacao }): Promise<boolean> => {
    const fallbackEtapa = opcoes?.fallbackEtapa ?? 2;
    if (!processoId) return false;
    try {
      const resp = await clienteApi.get(`/api/juridico/criacao-peticao/${encodeURIComponent(processoId)}/informacoes-adicionais`);
      setInfoAdicionais(resp.data as InformacoesAdicionaisSolicitadas);
      return true;
    } catch (err) {
      console.error('Erro ao buscar informações adicionais', err);
      const mensagem = axios.isAxiosError(err)
        ? (err.response?.data?.detail as string) || 'Erro ao buscar informações adicionais'
        : 'Erro ao buscar informações adicionais';
      await tratarFalhaProcessamento(mensagem, fallbackEtapa, { cancelarProcesso: false });
      return false;
    }
  }, [processoId, tratarFalhaProcessamento]);

  const iniciarProcessamentoFinal = async (dados?: Record<string, unknown>, documentosIds?: string[]): Promise<boolean> => {
    if (!processoId) return false;
    const payload = dados ?? informacoesAdicionaisFornecidas;
    try {
      await clienteApi.post(`/api/juridico/criacao-peticao/${encodeURIComponent(processoId)}/processar-final`, {
        informacoes_adicionais: payload,
        documentos_adicionais_ids: documentosIds ?? [],
      });
      setErro('');
      conectarWebSocket(processoId);
      return true;
    } catch (err) {
      console.error('Erro ao iniciar processamento final', err);
      const mensagem = axios.isAxiosError(err)
        ? (err.response?.data?.detail as string) || 'Erro ao iniciar processamento final'
        : 'Erro ao iniciar processamento final';
      await tratarFalhaProcessamento(mensagem, 4);
      return false;
    }
  };

  const buscarResultadoFinal = useCallback(async (): Promise<boolean> => {
    if (!processoId) return false;
    try {
      const resp = await clienteApi.get(`/api/juridico/criacao-peticao/${encodeURIComponent(processoId)}/resultado`);
      setResultado(normalizarResultadoCriacao(resp.data as Record<string, unknown>));
      return true;
    } catch (err) {
      console.error('Erro ao buscar resultado final', err);
      const mensagem = axios.isAxiosError(err)
        ? (err.response?.data?.detail as string) || 'Erro ao buscar resultado'
        : 'Erro ao buscar resultado';
      await tratarFalhaProcessamento(mensagem, 4, { cancelarProcesso: false });
      return false;
    }
  }, [processoId, tratarFalhaProcessamento]);

  const pollingStatusRef = useRef<number | null>(null);
  const pararPollingStatus = useCallback(() => {
    if (pollingStatusRef.current !== null) {
      window.clearInterval(pollingStatusRef.current);
      pollingStatusRef.current = null;
    }
  }, []);

  const iniciarPollingStatus = useCallback((pid: string) => {
    if (!pid) return;
    pararPollingStatus();

    const executarConsulta = async () => {
      try {
        const resp = await clienteApi.get(`/api/juridico/criacao-peticao/${encodeURIComponent(pid)}/status`);
        const payload = resp.data as {
          status?: string;
          progresso?: number;
          progresso_percentual?: number;
          etapa_atual?: string;
          mensagem?: string;
          mensagem_erro?: string;
        };

        const progresso = typeof payload.progresso_percentual === 'number'
          ? payload.progresso_percentual
          : typeof payload.progresso === 'number'
            ? payload.progresso
            : 0;
        if (!Number.isNaN(progresso)) setProgressoProcessamento(progresso);
        if (payload.etapa_atual) setEtapaProcessamento(payload.etapa_atual);

        const statusNormalizado = (payload.status || '').toLowerCase();
        if (statusNormalizado === 'concluido' || statusNormalizado === 'concluida' || progresso >= 100) {
          pararPollingStatus();
          const sucesso = await buscarResultadoFinal();
          if (sucesso) {
            avancarEtapa();
          }
          return;
        }

        if (statusNormalizado === 'aguardando_informacoes_adicionais') {
          pararPollingStatus();
          const carregouSolicitacao = await buscarInformacoesAdicionais({ fallbackEtapa: 4 });
          if (carregouSolicitacao) {
            setEtapaAtual(4);
            persistirEstadoWizard(4);
          }
          return;
        }

        if (statusNormalizado === 'erro') {
          pararPollingStatus();
          await tratarFalhaProcessamento(payload.mensagem ?? payload.mensagem_erro ?? 'Processamento interrompido.', etapaAtual >= 5 ? 4 : 2, { cancelarProcesso: false });
        }
      } catch (err) {
        console.warn('[CriacaoPeticaoInicial] Falha ao obter status via polling', err);
      }
    };

    executarConsulta();
    pollingStatusRef.current = window.setInterval(executarConsulta, 5000);
  }, [buscarResultadoFinal, tratarFalhaProcessamento, etapaAtual, avancarEtapa, pararPollingStatus, buscarInformacoesAdicionais, persistirEstadoWizard]);

  // ===== WebSocket =====
  const conectarWebSocket = useCallback((pidOverride?: string) => {
    const alvoProcesso = pidOverride || processoId;
    if (!alvoProcesso) return;
    try {
      if (wsRef.current) {
        wsRef.current.unsubscribe();
        wsRef.current.servico.release();
        wsRef.current = null;
      }
      const servico = obterWebSocketService(tokenRef.current || undefined, alvoProcesso);
      servico.connect();
      iniciarPollingStatus(alvoProcesso);
      const unsubscribe = servico.subscribe((update) => {
        const atualizacao = update as Partial<AtualizacaoStatusAnalise> & { tipo?: string; mensagem?: string; processo_id?: string };
        if (typeof atualizacao.progresso_percentual === 'number') setProgressoProcessamento(atualizacao.progresso_percentual);
        if (atualizacao.etapa_atual) setEtapaProcessamento(String(atualizacao.etapa_atual));
        if (atualizacao.status === 'CONCLUIDA') {
          pararPollingStatus();
          void (async () => {
            const sucesso = await buscarResultadoFinal();
            if (sucesso) {
              avancarEtapa();
            }
          })();
          return;
        }
        if (atualizacao.status === 'ERRO' || atualizacao.tipo === 'erro') {
          pararPollingStatus();
          const mensagemErro = (atualizacao.mensagem_erro || atualizacao.mensagem) as string | undefined;
          const fallbackEtapa: EtapaWizardCriacao = etapaAtual >= 5 ? 4 : 2;
          void tratarFalhaProcessamento(mensagemErro ?? 'Processamento interrompido.', fallbackEtapa, { cancelarProcesso: false });
          return;
        }
        if (atualizacao.mensagem_erro) setErro(String(atualizacao.mensagem_erro));
      });
      wsRef.current = { servico, unsubscribe };
    } catch (err) {
      console.warn('[CriacaoPeticaoInicial] Falha ao conectar WebSocket', err);
    }
  }, [processoId, etapaAtual, tratarFalhaProcessamento, buscarResultadoFinal, avancarEtapa, iniciarPollingStatus, pararPollingStatus]);

  useEffect(() => {
    return () => {
      // cleanup websocket
      if (wsRef.current) {
        wsRef.current.unsubscribe();
        wsRef.current.servico.release();
        wsRef.current = null;
      }
      pararPollingStatus();
    };
  }, [pararPollingStatus]);

  // ===== Re-hidratar estado a partir do localStorage ou query params =====
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const qpid = params.get('processo_id');
    if (!qpid) {
      // Nenhum processo na URL: começar um novo fluxo
      return;
    }
    if (processoSincronizadoRef.current === qpid) return;
    if (processosHidratadosRef.current.has(qpid)) return;

    processoSincronizadoRef.current = qpid;
    processosHidratadosRef.current.add(qpid);

    let cancelado = false;

    (async () => {
      try {
        const resp = await obterProcesso(qpid);
        if (cancelado) return;
        const processo = resp.data as { processo_id?: string; id?: string; status_processamento?: string; metadados?: Record<string, unknown>; mensagem_status?: string };
        const pidReal = processo.processo_id || processo.id || qpid;
        setProcessoId(pidReal);
        sincronizarProcessoNaUrl(pidReal);

        const metadados = (processo.metadados ?? {}) as Record<string, unknown>;
        if (metadados.contexto_inicial) {
          setContextoInicial(normalizarContextoDoBackend(metadados.contexto_inicial as Record<string, unknown>));
        }
        const docsPersistidos = (metadados.documentos_anexados_detalhes || metadados.documentos_anexados || metadados.documentos) as DocumentoAnexado[] | undefined;
        if (Array.isArray(docsPersistidos)) setDocumentosAnexados(docsPersistidos);

        const status = (processo.status_processamento || '').toLowerCase();
        if (status === 'processando_inicial') {
          setEtapaAtual(3);
          persistirEstadoWizard(3);
          conectarWebSocket(pidReal);
        } else if (status === 'aguardando_informacoes_adicionais') {
          pararPollingStatus();
          if (metadados.informacoes_adicionais_solicitadas) setInfoAdicionais(metadados.informacoes_adicionais_solicitadas as InformacoesAdicionaisSolicitadas);
          setEtapaAtual(4);
          persistirEstadoWizard(4);
        } else if (status === 'processando_final') {
          setEtapaAtual(5);
          persistirEstadoWizard(5);
          conectarWebSocket(pidReal);
        } else if (status === 'concluido') {
          pararPollingStatus();
          if (metadados.resultado_criacao_peticao) {
            setResultado(normalizarResultadoCriacao(metadados.resultado_criacao_peticao as Record<string, unknown>));
          }
          setEtapaAtual(6);
          persistirEstadoWizard(6);
        } else if (status === 'erro') {
          pararPollingStatus();
          const mensagemStatus = processo.mensagem_status || (metadados.mensagem_status as string) || undefined;
          await tratarFalhaProcessamento(mensagemStatus ?? 'Processo anterior falhou.', 2, { cancelarProcesso: false });
        }
      } catch (err) {
        console.warn('[CriacaoPeticaoInicial] Falha ao sincronizar processo persistido', err);
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [location.search, conectarWebSocket, tratarFalhaProcessamento, persistirEstadoWizard, pararPollingStatus, sincronizarProcessoNaUrl]);

  // ===== UI =====
  return (
    <ErrorBoundary componentName="Página de Criação de Petição">
      <div className="mx-auto max-w-6xl px-4 py-8">
        <header className="mb-6">
          <nav aria-label="Breadcrumb" className="mb-3 text-sm text-gray-500">
            <Link to="/dashboard" className="text-primary-600 hover:underline">Dashboard</Link>
            <span className="mx-2">/</span>
            <span>Criação de Petição Inicial</span>
          </nav>
          <h2 className="text-2xl font-semibold">Criação de Petição Inicial</h2>
          <p className="text-sm text-gray-600">Siga o fluxo para criar e gerar sua petição inicial com suporte de IA.</p>
        </header>

        {/* Stepper */}
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            {etapas.map((etapa, index) => (
              <div key={etapa.numero} className="flex flex-1 items-center">
                <div className="flex flex-col items-center">
                  <div className={`flex size-12 items-center justify-center rounded-full border-2 ${etapa.status === 'completed' ? 'border-green-500 bg-green-500 text-white' : etapa.status === 'in-progress' ? 'border-primary-500 bg-primary-500 text-white' : 'border-gray-300 bg-white text-gray-400'}`}>
                    {etapa.status === 'completed' ? (
                      <CheckCircle2 className="size-6" />
                    ) : (
                      <etapa.icone className="size-6" />
                    )}
                  </div>
                  <div className="mt-2 hidden min-h-[48px] text-center md:block">
                    <div className={`mx-auto max-w-[200px] whitespace-normal break-words text-sm font-medium ${etapa.status === 'in-progress' ? 'text-primary-600' : 'text-gray-600'}`}>{etapa.titulo}</div>
                  </div>
                </div>
                {index < etapas.length - 1 && <div className={`mx-4 h-0.5 flex-1 ${etapa.status === 'completed' ? 'bg-green-500' : 'bg-gray-300'}`} />}
              </div>
            ))}
          </div>
        </div>

        {/* Error */}
        {erro && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="flex items-start gap-3">
              <div className="shrink-0 text-red-600">⚠️</div>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-red-800">Erro</h3>
                <p className="mt-1 text-sm text-red-700">{erro}</p>
                <div className="mt-3 flex flex-wrap gap-2 text-sm">
                  {etapaAtual > 2 && etapaAtual < 6 && (
                    <button type="button" className="btn-secondary px-3 py-1" onClick={voltarParaUpload}>Voltar para upload</button>
                  )}
                  <button
                    type="button"
                    className="btn-secondary px-3 py-1"
                    onClick={() => { void reiniciarFluxo(); }}
                  >
                    Reiniciar fluxo completo
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className="card min-h-[360px] rounded-md bg-white p-6 shadow-sm">
          {etapaAtual === 1 && (
            <ComponenteContextoInicial
              contextoInicial={contextoInicial ?? undefined}
              onContextoConcluido={async (ctx) => {
                setContextoInicial(ctx);
                const pid = await criarProcessoBackend(ctx);
                if (pid) {
                  avancarEtapa();
                }
              }}
              onErro={(e) => setErro(e)}
            />
          )}

          {etapaAtual === 2 && (
            <ComponenteUploadDocumentos
              peticaoId={processoId}
              documentosExistentes={documentosAnexados}
              onDocumentosConcluidos={async (docs) => {
                setDocumentosAnexados(docs);
                const sucesso = await iniciarProcessamentoInicial(docs);
                if (sucesso) {
                  avancarEtapa();
                }
              }}
              onVoltar={voltarEtapa}
              onErro={(e) => setErro(e)}
            />
          )}

          {etapaAtual === 3 && (
            <ComponenteLoadingProcessamento
              peticaoId={processoId}
              etapaAtual={etapaProcessamento}
              progresso={progressoProcessamento}
              onProcessamentoConcluido={async () => {
                const sucesso = await buscarInformacoesAdicionais();
                if (sucesso) {
                  avancarEtapa();
                }
              }}
              onErro={handleProcessamentoErro}
            />
          )}

          {etapaAtual === 4 && informacoesAdicionaisSolicitadas && (
            <ComponenteInformacoesAdicionais
              peticaoId={processoId}
              informacoesSolicitadas={informacoesAdicionaisSolicitadas}
              onInformacoesConcluidas={async (dados, documentos) => {
                setInfoFornecidas(dados);
                // Extract document IDs if documents were provided
                const documentosIds = documentos?.map(d => d.id) ?? [];
                const sucesso = await iniciarProcessamentoFinal(dados, documentosIds);
                if (sucesso) {
                  avancarEtapa();
                }
              }}
              onVoltar={voltarEtapa}
              onErro={(e) => setErro(e)}
            />
          )}

          {etapaAtual === 5 && (
            <ComponenteLoadingProcessamento
              peticaoId={processoId}
              etapaAtual={etapaProcessamento}
              progresso={progressoProcessamento}
              onProcessamentoConcluido={async () => {
                const sucesso = await buscarResultadoFinal();
                if (sucesso) {
                  avancarEtapa();
                }
              }}
              onErro={handleProcessamentoErro}
            />
          )}

          {etapaAtual === 6 && resultado && (
            <ErrorBoundary componentName="Resultados da Análise">
              <ComponenteResultadosAnalise
                resultado={resultado}
                onNovaAnalise={() => {
                  void reiniciarFluxo();
                  navigate('/criacao-peticao-inicial', { replace: true });
                }}
                onEditarPeticao={(id) => navigate(`/edicao-peticao/${id}`)}
                perspectiva="autor"
              />
            </ErrorBoundary>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
}

export default CriacaoPeticaoInicial;
